import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { FileText, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { apiError, currentMonth } from "@/lib/format";
import { downloadBlob } from "@/lib/download";
import { PageHeader, EmptyState } from "@/components/Page";

export default function Lohnabrechnungen() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ user_id: "", month: currentMonth(), file: null });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setRows((await api.get("/payslips")).data || []);
    if (isAdmin) setEmployees(((await api.get("/employees")).data || []).filter((u) => u.role === "employee"));
  };
  useEffect(() => { load(); }, []);

  const empName = (id) => employees.find((e) => e.user_id === id)?.name || "";

  const upload = async () => {
    if (!form.user_id || !form.file) return toast.error("Mitarbeiter und Datei erforderlich");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("user_id", form.user_id);
      fd.append("month", form.month);
      fd.append("file", form.file);
      await api.post("/payslips", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Lohnabrechnung hochgeladen");
      setOpen(false);
      setForm({ user_id: "", month: currentMonth(), file: null });
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const download = async (p) => {
    try { await downloadBlob(`/payslips/${p.id}/download`, p.filename || "lohnabrechnung.pdf"); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader
        title="Lohnabrechnungen"
        subtitle={isAdmin ? "Lade Lohnabrechnungen für dein Team hoch." : "Deine Dokumente zum Herunterladen."}
        testid="lohn-header"
        action={isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="payslip-open" className="brand-bg text-white hover:opacity-90"><Upload className="h-4 w-4 mr-2" /> Hochladen</Button></DialogTrigger>
            <DialogContent data-testid="payslip-dialog">
              <DialogHeader><DialogTitle>Lohnabrechnung hochladen</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Mitarbeiter</Label>
                  <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                    <SelectTrigger data-testid="payslip-employee"><SelectValue placeholder="Wählen…" /></SelectTrigger>
                    <SelectContent>{employees.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Monat</Label><Input type="month" data-testid="payslip-month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} /></div>
                <div className="space-y-2"><Label>Datei (PDF)</Label><Input type="file" accept="application/pdf,image/*" data-testid="payslip-file" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} /></div>
              </div>
              <DialogFooter><Button onClick={upload} disabled={busy} data-testid="payslip-upload" className="brand-bg text-white hover:opacity-90">{busy ? "Lädt…" : "Hochladen"}</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="grid gap-3 max-w-2xl">
        {rows.length === 0 ? (
          <Card className="border-slate-200 rounded-xl">
            <EmptyState icon={FileText} title="Noch keine Lohnabrechnungen" subtitle={isAdmin ? "Lade das erste Dokument hoch." : "Sobald deine Firma Dokumente hochlädt, erscheinen sie hier."} testid="lohn-empty" />
          </Card>
        ) : (
          rows.map((r) => (
            <Card key={r.id} className="p-4 border-slate-200 rounded-xl flex items-center justify-between" data-testid={`payslip-row-${r.id}`}>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center brand-text"><FileText className="h-5 w-5" /></div>
                <div>
                  <div className="font-medium text-slate-900">{r.month}{isAdmin && empName(r.user_id) ? ` · ${empName(r.user_id)}` : ""}</div>
                  <div className="text-sm text-slate-500">{r.filename}</div>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => download(r)} data-testid={`payslip-download-${r.id}`}>
                <Download className="h-4 w-4 mr-2" /> Herunterladen
              </Button>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
